import { MediaMatcher } from '@angular/cdk/layout';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  Signal,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatTooltip } from '@angular/material/tooltip';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import {
  FeatureEnabledDirective,
  FeatureInfoComponent,
} from '@principle-theorem/ng-feature-flags';
import {
  IntercomService,
  IntercomTargetDirective,
} from '@principle-theorem/ng-intercom';
import { MarketingCampaignService } from '@principle-theorem/ng-marketing';
import {
  BrandRouterLinkDirective,
  CampaignStatusComponent,
  IconChipComponent,
  InformationBoxComponent,
  ManagementService,
  OrganisationService,
  StateBasedNavigationService,
} from '@principle-theorem/ng-principle-shared';
import {
  BreadcrumbsComponent,
  MediaBreakpoint,
  NgCommonModule,
  type IBreadcrumb,
} from '@principle-theorem/ng-shared';
import {
  Brand,
  BrandLinks,
  CustomReport,
  ReportRun,
} from '@principle-theorem/principle-core';
import {
  MARKETING_CAMPAIGNS_FEATURE,
  ReportingPermissions,
} from '@principle-theorem/principle-core/features';
import {
  CUSTOM_REPORT_STATUS_COLOUR_MAP,
  CustomReportStatus,
  IMarketingCampaign,
  IReportRun,
  MarketingCampaignStatus,
  REPORT_RUN_RETENTION_DAYS,
  RestrictAccessEntity,
  TemplateScope,
  type ICustomReport,
} from '@principle-theorem/principle-core/interfaces';
import {
  IReportBuilderDisplay,
  REPORT_BUILDER_DATA_SOURCES,
  ReportBuilderDataSourceInstance,
  resolveDataSource,
  toCustomReportQuery,
} from '@principle-theorem/reporting';
import {
  asReffable,
  doc,
  FieldValue,
  filterUndefined,
  Firestore,
  ITimePeriod,
  snapshot,
  Timestamp,
  toTimePeriod,
  where,
  type WithRef,
} from '@principle-theorem/shared';
import { isEqual } from 'lodash';
import * as moment from 'moment-timezone';
import { combineLatest, type Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take } from 'rxjs/operators';
import {
  getReportBuilderLoader,
  ILoaderDisplay,
} from '../../report-builder/report-builder-loader/report-builder-loader';
import { ReportBuilderStore } from '../../report-builder/report-builder.store';
import { ReportEditorService } from '../../report-builder/report-editor.service';
import { CurrentCustomReportScope } from '../current-custom-report-scope';
import { CustomReportsService } from '../custom-reports.service';
import { CustomReportChartsComponent } from './custom-report-charts/custom-report-charts.component';
import { CustomReportFiltersComponent } from './custom-report-filters/custom-report-filters.component';
import { CustomReportScopesComponent } from './custom-report-scopes/custom-report-scopes.component';
import { CustomReportTableComponent } from './custom-report-table/custom-report-table.component';
import { DataSourceDisplayComponent } from './data-source-display/data-source-display.component';
import { QueryFilterDisplayComponent } from './query-filter-display/query-filter-display.component';
import { ReportRunHistoryComponent } from './report-run-history/report-run-history.component';
import { HistoricalRunBannerComponent } from './report-run-history/historical-run-banner.component';
import {
  IRunReportEvent,
  RunCustomReportFormComponent,
} from './run-custom-report-form/run-custom-report-form.component';

@Component({
  selector: 'pr-custom-report',
  templateUrl: './custom-report.component.html',
  styleUrls: ['./custom-report.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'flex flex-col h-full w-full',
  },
  imports: [
    BrandRouterLinkDirective,
    BreadcrumbsComponent,
    CampaignStatusComponent,
    CustomReportChartsComponent,
    CustomReportFiltersComponent,
    CustomReportScopesComponent,
    CustomReportTableComponent,
    FeatureEnabledDirective,
    FeatureInfoComponent,
    HistoricalRunBannerComponent,
    IconChipComponent,
    InformationBoxComponent,
    MatButtonModule,
    MatIcon,
    MatProgressBar,
    MatSidenavModule,
    MatTooltip,
    NgCommonModule,
    QueryFilterDisplayComponent,
    ReportRunHistoryComponent,
    RouterModule,
    RunCustomReportFormComponent,
    DataSourceDisplayComponent,
    IntercomTargetDirective,
  ],
})
export class CustomReportComponent {
  private _currentCustomReportScope = inject(CurrentCustomReportScope);
  private _organisation = inject(OrganisationService);
  private _management = inject(ManagementService);
  private _reportEditor = inject(ReportEditorService);
  private _marketingService = inject(MarketingCampaignService);
  private _stateNav = inject(StateBasedNavigationService);
  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private _destroyRef = inject(DestroyRef);
  store = inject(ReportBuilderStore);
  customReportsService = inject(CustomReportsService);
  intercom = inject(IntercomService);

  customReport$: Observable<WithRef<ICustomReport>>;

  breadcrumbs = computed<IBreadcrumb[]>(() => {
    const report = this.customReport();
    const basePath = ['/', 'reporting', 'custom-reports'];
    const crumbs: IBreadcrumb[] = [
      { label: 'Reporting' },
      { label: 'Custom Reports', path: basePath },
    ];
    if (this.isViewingHistoricalRun() && report) {
      crumbs.push({
        label: report.name,
        path: [...basePath, report.ref.id],
      });
    }
    return crumbs;
  });

  currentBreadcrumb = computed(() => {
    if (this.isViewingHistoricalRun()) {
      return 'History';
    }
    return this.customReport()?.name ?? '';
  });
  brandLinks = BrandLinks;
  loader$: Observable<ILoaderDisplay | undefined>;
  hasEditPermissions$: Observable<boolean>;
  marketingFeature = MARKETING_CAMPAIGNS_FEATURE;

  dateRange = signal<ITimePeriod | undefined>(undefined);
  datasource: Signal<ReportBuilderDataSourceInstance | undefined>;
  customReport: Signal<WithRef<ICustomReport> | undefined>;
  activeHistoricalRun: Signal<WithRef<IReportRun> | undefined>;
  effectiveCustomReport: Signal<WithRef<ICustomReport> | undefined>;
  templateScopes: Signal<TemplateScope[]>;
  marketingCampaigns: Signal<WithRef<IMarketingCampaign>[]>;
  draftMarketingCampaigns: Signal<WithRef<IMarketingCampaign>[]>;
  isViewingHistoricalRun = computed(() => !!this.activeHistoricalRun());
  isInDraft = computed(
    () => this.customReport()?.status === CustomReportStatus.Draft
  );
  reportStatus = CustomReportStatus;
  reportStatusColourMap = CUSTOM_REPORT_STATUS_COLOUR_MAP;

  sidenav = viewChild<MatSidenav>('sidenav');
  breakpoint = new MediaBreakpoint(
    inject(MediaMatcher),
    inject(DestroyRef),
    '1200px'
  );

  constructor() {
    this.store.reset();
    this.customReport$ = this._currentCustomReportScope
      .asObservable$()
      .pipe(filterUndefined());

    this.customReport$
      .pipe(take(1), takeUntilDestroyed())
      .subscribe((report) => this.store.setCurrentReport(report));

    this._loadHistoricalRunFromRoute();

    this.customReport = toSignal(this.customReport$);
    this.activeHistoricalRun = toSignal(this.store.activeHistoricalRun$);
    this.effectiveCustomReport = computed(() => {
      const report = this.customReport();
      const run = this.activeHistoricalRun();
      if (!report || !run) {
        return report;
      }
      const reportSnapshot = run.reportSnapshot;
      if (!reportSnapshot) {
        return report;
      }
      return {
        ...report,
        ...(reportSnapshot.filters ? { staticFilters: reportSnapshot.filters } : {}),
        ...(reportSnapshot.columns ? { columns: reportSnapshot.columns } : {}),
        ...(reportSnapshot.chartSections
          ? { chartSections: reportSnapshot.chartSections }
          : {}),
      };
    });
    this.datasource = computed(() => {
      const report = this.customReport();
      return report
        ? resolveDataSource(report.dataSource, REPORT_BUILDER_DATA_SOURCES)
        : undefined;
    });

    const campaigns$ = this.customReport$.pipe(
      switchMap((report) => {
        const brand = asReffable(CustomReport.brandRef(report));
        return Firestore.getDocs(
          Brand.marketingCampaignCol(brand),
          where('customReportRef', '==', report.ref)
        );
      })
    );
    this.marketingCampaigns = toSignal(campaigns$, { initialValue: [] });
    this.draftMarketingCampaigns = computed(() =>
      this.marketingCampaigns().filter(
        (campaign) => campaign.status === MarketingCampaignStatus.Draft
      )
    );

    this.templateScopes = computed(() => this.datasource()?.scopes ?? []);

    this.breakpoint.isActive$
      .pipe(takeUntilDestroyed())
      .subscribe((isActive) => void this._handleBreakpointChange(isActive));

    this.loader$ = this.store.state$.pipe(
      map((state) => getReportBuilderLoader(state))
    );

    this.store.display$
      .pipe(
        filterUndefined(),
        distinctUntilChanged((a, b) => this._isSameDisplay(a, b)),
        filter(() => !this.isViewingHistoricalRun()),
        takeUntilDestroyed()
      )
      .subscribe((display) => void this.updateReport(display));

    this.hasEditPermissions$ = this._organisation.userHasPermission$(
      ReportingPermissions.ReportingCustomReportEdit
    );
  }

  async publishOpenCampaign(
    campaign: WithRef<IMarketingCampaign>
  ): Promise<void> {
    const report = this.customReport();
    if (!report) {
      return;
    }
    if (report?.status !== CustomReportStatus.Published) {
      await this.publishReport(report);
    }

    const campaignLink = this.brandLinks.marketingCampaign(campaign);
    await this._stateNav.brand(campaignLink.segments, {
      fragment: campaignLink.fragment,
    });
  }

  async editCustomReport(customReport: WithRef<ICustomReport>): Promise<void> {
    const reloadReport =
      await this.customReportsService.editCustomReport(customReport);
    if (reloadReport) {
      this.store.setCurrentReport(reloadReport);
      await this.store.reloadReport({ clearCurrentResults: true });
    }
  }

  getAccessIcon(accessEntity: RestrictAccessEntity): string {
    switch (accessEntity.type) {
      case 'staffer':
        return 'person';
      case 'role':
        return 'group';
      default:
        return 'lock_open';
    }
  }

  async runReport(event?: IRunReportEvent): Promise<void> {
    const customReport = await snapshot(this.customReport$);
    const isAllowed = await this._canRunReport(customReport);
    if (!isAllowed) {
      return;
    }
    const currentQuery = await snapshot(this.store.query$);
    const queryScopes = event?.queryScopes ?? currentQuery?.scopeRequests;
    if (!queryScopes) {
      return;
    }
    const filters = event?.filters ?? currentQuery?.staticFilters;
    const query = toCustomReportQuery(customReport, queryScopes, filters);

    if (!query) {
      return;
    }
    query.filterDate =
      event?.filterDate ?? currentQuery?.filterDate ?? moment();
    query.filterDateRange =
      event?.filterDateRange ??
      currentQuery?.filterDateRange ??
      toTimePeriod(moment(), moment());
    this.store.loadQuery(query);
  }

  async publishReport(customReport: WithRef<ICustomReport>): Promise<void> {
    const response = await this._reportEditor.openCustomReportDialog({
      customReport,
      isPublishing: true,
    });
    if (!response || !response.formData) {
      return;
    }
    await this.updateReport({
      ...response.formData,
      status: CustomReportStatus.Published,
    });
  }

  async updateReport(updates: Partial<ICustomReport>): Promise<void> {
    const report = this.customReport();
    if (!report) {
      return;
    }
    await Firestore.patchDoc(report.ref, updates);

    await this.store.reloadReport();
  }

  loadHistoricalRun(run: WithRef<IReportRun>): void {
    if (!run.result?.storageUrl) {
      return;
    }
    this.store.loadStorageResults(run);
  }

  clearHistoricalRun(): void {
    this.store.patchState({ activeHistoricalRun: undefined });
    const report = this.customReport();
    if (report && this._route.snapshot.paramMap.has('runId')) {
      void this._router.navigate(
        ['/', 'reporting', 'custom-reports', report.ref.id],
        { replaceUrl: true }
      );
    }
  }

  async backToCurrentReport(): Promise<void> {
    this.clearHistoricalRun();
    await this.runReport();
  }

  async toggleHistoricalRunPin(): Promise<void> {
    const run = this.activeHistoricalRun();
    if (!run) {
      return;
    }
    const pinned = !run.pinned;
    const newExpiresAt = pinned
      ? undefined
      : Timestamp.fromDate(
          moment().add(REPORT_RUN_RETENTION_DAYS, 'days').toDate()
        );
    await Firestore.patchDoc(run.ref, {
      pinned,
      expiresAt: newExpiresAt ?? FieldValue.delete(),
    });
    this.store.patchState({
      activeHistoricalRun: {
        ...run,
        pinned,
        expiresAt: newExpiresAt,
      },
    });
  }

  async archiveHistoricalRun(): Promise<void> {
    const run = this.activeHistoricalRun();
    if (!run) {
      return;
    }
    await Firestore.patchDoc(run.ref, {
      archivedAt: Timestamp.now(),
      pinned: false,
      expiresAt: FieldValue.delete(),
    });
    this.clearHistoricalRun();
  }

  async createCampaign(): Promise<void> {
    const report = this.customReport();
    if (!report) {
      return;
    }
    await this._marketingService.addCampaignFromReport(report.ref);
  }

  private async _canRunReport(
    customReport: WithRef<ICustomReport>
  ): Promise<boolean> {
    const managementUser = await snapshot(this._management.user$);
    if (managementUser) {
      return true;
    }
    const staffer = await snapshot(this._organisation.staffer$);
    return CustomReport.userHasAccess(customReport, staffer);
  }

  private _loadHistoricalRunFromRoute(): void {
    combineLatest([this.customReport$, this._route.paramMap])
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(async ([report, paramMap]) => {
        const runId = paramMap.get('runId');
        if (!runId) {
          if (this.activeHistoricalRun()) {
            this.store.patchState({ activeHistoricalRun: undefined });
          }
          return;
        }
        const runRef = doc(ReportRun.col(report), runId);
        try {
          const run = await Firestore.getDoc(runRef);
          this.loadHistoricalRun(run);
        } catch {
          this.clearHistoricalRun();
        }
      });
  }

  private async _handleBreakpointChange(isActive: boolean): Promise<void> {
    if (isActive) {
      await this.sidenav()?.close();
    }
  }

  private _isSameDisplay(
    a: IReportBuilderDisplay,
    b: IReportBuilderDisplay
  ): boolean {
    return (
      isEqual(a.columns, b.columns) && isEqual(a.chartSections, b.chartSections)
    );
  }
}
